import type { TFunction } from 'i18next';

import { ApiError } from './api/client.js';

/** The `reason` of a `409 CONFLICT` with a sentence of its own (api-spec §2). */
const CONFLICTS = new Map([
  ['stripe_pending', 'stripePending'],
  ['stripe_active', 'stripeActive'],
  ['access_active', 'accessActive'],
  ['invoice_status', 'invoiceStatus'],
  // Telegram rejects the bot token: no new chat, link or test message for now (SP-A2.251).
  ['telegram_unavailable', 'telegramUnavailable'],
  ['stale_record', 'staleRecord'],
  // The billing address changed while an invoice of the operator was made (review of SP-A2.247).
  ['address_changed', 'addressChanged'],
]);

/** The `reason` of a refusal (`409 CONFLICT`), if the API named one. */
export const conflictReason = (error: unknown) =>
  error instanceof ApiError && error.code === 'CONFLICT'
    ? (error.problem as { reason?: string }).reason
    : undefined;

/**
 * The record as it is now, when a save of a form was refused as made on an older version of it
 * (409 `stale_record`, SP-A2.193): the form shows it and saves on from it.
 */
export const staleRecord = <T>(error: unknown): T | undefined =>
  conflictReason(error) === 'stale_record'
    ? ((error as ApiError).problem as { current?: T }).current
    : undefined;

/** A sentence for an API error in the user's language (api-spec §2 codes). */
export function errorMessage(error: unknown, t: TFunction): string {
  if (!(error instanceof ApiError)) return t('errors.INTERNAL');
  if (error.code === 'WEAK_PASSWORD') {
    const reason = (error.problem as { reason?: string }).reason;
    if (reason)
      return t(`errors.weakPassword.${reason}`, { defaultValue: t('errors.WEAK_PASSWORD') });
  }
  // Passwords from a session: a few per 15 minutes per person (SP-A2.176), not "a moment".
  if (error.code === 'RATE_LIMITED' && (error.problem as { reason?: string }).reason === 'password')
    return t('errors.passwordChecks');
  // Why the admin refuses a step for the account (SP-A2.214): beside a Stripe checkout still
  // being paid (SP-A2.219) or a running subscription, a trial beside paid access, an invoice
  // booked or closed meanwhile; a form saved on a record changed meanwhile (SP-A2.193). The
  // customer's Checkout says the ones it meets in its own words: `stripe_pending`,
  // `stripe_active` and – online payment beside a plan on invoice – `access_active` (review of
  // SP-A2.214).
  const conflict = CONFLICTS.get(conflictReason(error) ?? '');
  if (conflict) return t(`errors.${conflict}`);
  // Stripe could not be asked whether such a checkout runs (SP-A2.221): not booked blind.
  if (
    error.code === 'UNAVAILABLE' &&
    (error.problem as { reason?: string }).reason === 'stripe_unavailable'
  )
    return t('errors.stripeUnavailable');
  // A VAT ID of another country than the address is no "not valid" of the register (SP-A2.79).
  if (
    error.code === 'VAT_ID_INVALID' &&
    (error.problem as { reason?: string }).reason === 'country'
  )
    return t('errors.vatIdCountry');
  return t(`errors.${error.code}`, { defaultValue: t('errors.INTERNAL') });
}
